<template>
  <parent>
    <div
      v-if="!fetchingApiKey && rolesReady"
      class="h-100 d-flex flex-column col-lg-9 m-auto"
    >
      <div class="d-flex flex-wrap justify-content-between">
        <div class="mb-1">
          <b-button
            variant="bg-white"
            class="mr-1"
            :to="{ name: 'api-keys' }"
          >
            <GjIcon
              name="ArrowLeft"
              class="icon-left"
            />
            API Keys
          </b-button>
        </div>
        <div class="d-flex">
          <b-button
            variant="bg-white"
            class="mr-1"
            :to="{ name: 'edit-api-key', params: { id: $route.params.id } }"
          >
            <GjIcon
              name="Edit"
              class="icon-left"
            />
            Edit
          </b-button>
          <b-button
            variant="bg-white"
            @click="deleteApiKey($route.params.id)"
          >
            <GjIcon
              name="Delete"
              class="icon-left"
            />
            Delete
          </b-button>
        </div>
      </div>
      <paper-simple class="flex-fill">
        <div class="main">
          <input-field
            name="api_key-name"
            label="Name"
            label-position="top"
          >
            {{ apiKey.name }}
          </input-field>
          <input-field
            name="api_key-role"
            label="Role"
            label-position="top"
          >
            {{ getRoleName }}
          </input-field>
          <input-field
            label="Scope"
            label-position="top"
          >
            {{ getObjectOptionsText(apiKey.apiKeyScopeOptions, apiKey.scope) }}
          </input-field>
          <input-field
            name="api_key-validUntil"
            label="Valid Until"
            label-position="top"
          >
            {{ formatOffsetDateTime(apiKey.validUntil) }}
          </input-field>
          <div class="mb-1">
            <b-button
              variant="bg-white"
              @click="showApiKey = true"
            >
              <GjIcon
                name="InfoAlt"
                class="icon-left"
              />
              Get API Key
            </b-button>
          </div>
          <custom-alert
            icon-name="InfoAlt"
            variant="default"
            icon-color="#e34850"
          >
            {{ warningMessage }}
          </custom-alert>
          <b-modal
            v-model="showApiKey"
            :title="apiKey.name"
            centered
            ok-title="Close"
            ok-variant="bg-white"
            ok-only
          >
            <input-field
              name="api_key-name"
              label="API Key"
              label-position="top"
            >
              <b-input-group>
                <template #append>
                  <b-button
                    v-clipboard:copy="apiKey.key"
                    v-clipboard:success="onCopy"
                    variant="bg-gray"
                    class="border-left-0"
                  >
                    <span>
                      <GjIcon name="Copy" />
                    </span>
                  </b-button>
                </template>
                <b-form-input
                  id="api_key-key"
                  :value="apiKey.key"
                  class="mr-0"
                  readonly
                />
              </b-input-group>
            </input-field>
            <custom-alert
              icon-name="InfoAlt"
              icon-color="#e34850"
            >
              {{ warningMessage }}
            </custom-alert>
          </b-modal>
        </div>
        <span class="bg-icon">
          <GjIcon
            name="Api"
            size="140"
            class="img-fluid"
          />
        </span>
      </paper-simple>
    </div>
    <loading-page v-else />
  </parent>
</template>

<script>
import redirectError from '@/services/redirectError'

export default {
  name: 'ApiKeyView',
  data() {
    return {
      warningMessage:
        'When you use API keys in your applications, ensure that they are kept secure during both storage and transmission. Publicly exposing your credentials is forbidden and can result in your account being compromised, which could lead to unexpected charges on your account.',
      loading: true,
      keyName: '',
      validUntil: null,
      role: null,
      roleOptions: [{ value: null, text: 'Select Role' }],
      rolesReady: false,
      showApiKey: false,
    }
  },
  computed: {
    apiKey() {
      return this.$store.state.apiKeys.apiKey
    },
    fetchingApiKey() {
      return this.$store.state.apiKeys.fetchingApiKey
    },
    getRoleName() {
      return (
        this.roleOptions.find(role => role.value === this.apiKey.role)?.text
        || '-'
      )
    },
  },
  created() {
    this.fetchApiKey()

    if (this.apiKey) {
      this.keyName = this.apiKey.name
      this.role = this.apiKey.role
      this.validUntil = this.apiKey.validUntil
    }
    this.$store
      .dispatch('roles/fetchRoles', {
        pageIndex: 0,
        pageSize: 500,
      })
      .then(() => {
        const role = this.$store.state.roles.roles
        for (let i = 0; i < role.length; i++) {
          this.roleOptions.push({ value: role[i].id, text: role[i].name })
        }
        this.rolesReady = true
      })
  },
  methods: {
    fetchApiKey() {
      if (this.$route.params.id !== undefined) {
        this.$store
          .dispatch('apiKeys/fetchApiKey', this.$route.params.id)
          .catch(err => redirectError(err, this.$router, this.$route.path))
      }
    },
    onCopy() {
      this.infoToast('Copied to clipboard', 'Clipboard')
    },
    deleteApiKey(id) {
      this.confirmDelete(
        'Delete',
        'Are you sure you want to delete this key?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('apiKeys/deleteApiKey', id)
            .then(() => {
              this.$router.push({
                name: 'api-keys',
                params: {
                  toast: {
                    title: 'Success',
                    variant: 'success',
                    message: 'API Key deleted successfully!',
                  },
                },
              })
            })
            .catch(() => {
              this.errorToast(
                "An error occurred. Couldn't delete the API Key. ",
              )
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.main {
  position: relative;
  z-index: 1;
}
</style>
